<template>
  <div v-if="loading">
    <ife-skeleton-input
      height="31px"
      class="mb-3"
    />
  </div>

  <ife-input-wrapper
    v-else
    :hide-label="hideLabel"
    :label="label"
    :label-cols="labelCols"
    :label-for="localId"
    :label-size="labelSize"
    :rules="rules"
    :warning="warning"
    :hint="hint"
  >
    <template #default="{ v }">
      <b-form-select
        :id="localId"
        v-model="localValue"
        :class="inputClass"
        :disabled="disabled"
        :name="localName"
        :options="formattedOptions"
        :readonly="readonly"
        :size="size"
        :state="rules ? getValidationState(v) : null"
        :text-field="groupField ? 'text' : textField"
        :value-field="groupField ? 'value' : valueField"
      >
        <template #first>
          <slot name="first">
            <option
              v-if="defaultFirstOption"
              :disabled="!allowNull"
              :value="null"
            >
              {{ defaultFirstOptionText }}
            </option>
          </slot>
        </template>
      </b-form-select>
    </template>
  </ife-input-wrapper>
</template>

<script>
export default {
  name: 'IFESimpleSelect',
  props: {
    allowNull: {
      type: Boolean,
      default: false,
    },
    defaultFirstOption: {
      type: Boolean,
      default: true,
    },
    defaultFirstOptionText: {
      type: String,
      default: 'Please select an option',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    groupField: {
      type: String,
      default: null,
    },
    groupLabelField: {
      type: String,
      default: 'name',
    },
    groupSort: {
      type: Array,
      default: null,
    },
    hideLabel: {
      type: Boolean,
      default: false,
    },
    hint: {
      type: String,
      default: null,
    },
    id: {
      type: String,
      default: null,
    },
    inputClass: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      required: true,
    },
    labelCols: {
      type: Number,
      default() {
        return 3;
      },
    },
    labelSize: {
      type: String,
      default() {
        return 'sm';
      },
    },
    loading: {
      type: Boolean,
      default() {
        return false;
      },
    },
    name: {
      type: String,
      default: null,
    },
    options: {
      type: Array,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    returnObject: {
      type: Boolean,
      default: true,
    },
    rules: {
      type: [String, Object],
      default: null,
    },
    size: {
      type: String,
      default() {
        return 'sm';
      },
    },
    textField: {
      type: String,
      default() {
        return 'name';
      },
    },
    value: {
      type: [String, Number, Object, Boolean, Array],
      default: null,
    },
    valueField: {
      type: String,
      default() {
        return 'id';
      },
    },
    warning: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      localId: null,
      localName: null,
      localValue: null,
    };
  },
  computed: {
    formattedOptions() {
      if (!this.groupField) {
        return this.options;
      }

      const groups = this.groupOptionsByField();
      const sortedNames = this.sortGroupNames(Object.keys(groups));

      return sortedNames.map((groupLabel) => ({
        label: groupLabel,
        options: groups[groupLabel].slice().sort((a, b) =>
          (a.text ?? '').localeCompare(b.text ?? ''),
        ),
      }));
    },
  },
  watch: {
    id: {
      immediate: true,
      handler: function(value) {
        if (null === value) {
          this.localId = this.labelToId(this.label);
        } else {
          this.localId = value;
        }
      },
    },
    localValue: {
      handler: function(value) {
        if (this.groupField && value) {
          const {groupLabel, optionValue} = this.parseGroupedValue(value);
          this.$emit('group-select', groupLabel);
          this.$emit('input', this.returnObject ? this.getSelectedItemObject(optionValue) : optionValue);
          return;
        }

        this.$emit('input', this.returnObject ? this.getSelectedItemObject(value) : value);
      },
    },
    name: {
      immediate: true,
      handler: function(value) {
        if (null === value) {
          this.localName = this.labelToId(this.label);
        } else {
          this.localName = value;
        }
      },
    },
    value: {
      immediate: true,
      handler: function(value) {
        this.setLocalValue(value);
      },
    },
  },
  created() {
    this.init();
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      this.setLocalValue(this.value);
    },
    labelToId(label) {
      return label.replace(' ', '-').toLowerCase();
    },
    buildGroupedValue(groupLabel, optionValue) {
      return groupLabel + '||' + optionValue;
    },
    parseGroupedValue(groupedValue) {
      const index = groupedValue.indexOf('||');
      return {
        groupLabel: groupedValue.substring(0, index),
        optionValue: groupedValue.substring(index + 2),
      };
    },
    groupOptionsByField() {
      const groups = {};

      for (const option of this.options) {
        for (const group of (option[this.groupField] ?? [])) {
          const groupLabel = group[this.groupLabelField] ?? 'Other';

          if (!groups[groupLabel]) {
            groups[groupLabel] = [];
          }

          groups[groupLabel].push({
            text: option[this.textField],
            value: this.buildGroupedValue(groupLabel, option[this.valueField]),
          });
        }
      }

      return groups;
    },
    sortGroupNames(groupNames) {
      if (!this.groupSort) {
        return groupNames.slice().sort((a, b) => a.localeCompare(b));
      }

      const prioritised = this.groupSort.filter((name) => groupNames.includes(name));
      const remaining = groupNames.filter((name) => !this.groupSort.includes(name));
      remaining.sort((a, b) => a.localeCompare(b));

      return [...prioritised, ...remaining];
    },
    getSelectedItemObject(value) {
      if (0 < this.options.length) {
        return this.options.filter((item) => {
          return item[this.valueField] === value;
        })[0];
      }

      return this.value;
    },
    findFirstGroupedValue(optionValue) {
      for (const group of this.formattedOptions) {
        const match = (group.options ?? []).find(
            (opt) => this.parseGroupedValue(opt.value).optionValue === optionValue,
        );

        if (match) {
          return match.value;
        }
      }

      return null;
    },
    setLocalValue(value) {
      if (value instanceof Object && value.hasOwnProperty(this.valueField)) {
        this.localValue = this.groupField ?
            this.findFirstGroupedValue(value[this.valueField]) :
            value[this.valueField];
      } else if (!this.returnObject) {
        this.localValue = value;
      }
    },
  },
};
</script>

<style scoped>

</style>
